/* eslint-disable */
import { useNavigate, useLocation } from "react-router-dom";
import { useAppSelector } from "../../../store/hooks";
import { EventRoundTripComponent as EventRoundTripComponent_ } from "../interfaces/base";
import styled from "@emotion/styled";
import RenderChildren from "../RenderChildren";
import usePageLanguage from "../hooks/usePageLanguage";
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useLayoutEffect,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import { useShoppingCart } from "../context/ShoppingCartContext";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { Spinner as BootstrapSpinner } from "react-bootstrap";
import "./EventRoundTripComponent.css";
import {
  getComponentFromStructureMap,
  matchesEditorTarget,
} from "../utilities/componentIdentity";
import {
  collectEventTicketProductUuids,
  eventCartMutationTarget,
  getEventCartInputKey,
  getEventCartLineQuantity,
  getEventTicketCartQuantity,
  resolveEventProductUuid,
} from "../utilities/eventCart";
import {
  clampQuantityToMinAndCap,
  hasMinOrderQtyConstraint,
  isEffectivelySoldOut,
  parseMaxOrderQty,
  resolveDisplayAvailable,
  resolveEffectiveMinQty,
  resolvePickerDisplayAvailable,
  resolveStartQuantity,
} from "../utilities/quantityCaps";
import { Info, Check } from "@mui/icons-material";
import {
  computePosition,
  flip,
  shift,
  offset,
  autoUpdate,
} from "@floating-ui/react";

// ── API helpers (reused from EventComponent) ────────────────────────────

declare var defaultApiConfig: any;
declare var API: any;

function fetchApi<TResponse>(
  url: string,
  config: RequestInit = {},
  jsonType: boolean = true
): Promise<TResponse> {
  const newConfig = {
    ...(typeof defaultApiConfig !== "undefined" ? defaultApiConfig : {}),
    ...config,
    headers: {
      ...(typeof defaultApiConfig !== "undefined" && defaultApiConfig.headers
        ? defaultApiConfig.headers
        : {}),
      ...(config.headers || {}),
    },
  };
  const apiUrl = typeof API !== "undefined" && API.url ? API.url : "";
  if (!jsonType) {
    return fetch(`${apiUrl}${url}`, newConfig).then(
      (data) => data as unknown as TResponse
    );
  }
  return fetch(`${apiUrl}${url}`, newConfig)
    .then((response) => response.json())
    .then((data) => data as TResponse);
}

function getHeaders(params: {} = {}) {
  return {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(typeof defaultApiConfig !== "undefined" &&
      defaultApiConfig.headers &&
      defaultApiConfig.headers.Authorization
        ? { Authorization: defaultApiConfig.headers.Authorization }
        : {}),
    },
    params: params,
  };
}

function fetchApiGet<TResponse>(
  url: string,
  params: {} = {}
): Promise<TResponse> {
  return fetchApi(url, getHeaders(params));
}

// ── Cache ───────────────────────────────────────────────────────────────

interface CacheEntry {
  data: any;
  timestamp: number;
  expiresIn: number;
}
const axiosCache = new Map<string, CacheEntry>();
const CACHE_DURATION = 5 * 60 * 1000;

async function cachedAxiosGet(url: string): Promise<any> {
  const cached = axiosCache.get(url);
  const now = Date.now();
  if (cached && now < cached.timestamp + cached.expiresIn) {
    return cached.data;
  }
  // @ts-ignore
  const response = await window.axios.get(url);
  axiosCache.set(url, {
    data: response,
    timestamp: now,
    expiresIn: CACHE_DURATION,
  });
  return response;
}

// ── Styled wrapper ──────────────────────────────────────────────────────

const Div = styled("div")(
  null,
  (props) =>
    function (props: any) {
      return { ...props["data-style"] };
    }
);

// ── Helpers ─────────────────────────────────────────────────────────────

function formatPrice(price: any) {
  return parseFloat(price).toFixed(2);
}

function formatTime(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleString("et-EE", { hour: "2-digit", minute: "2-digit" });
}

function formatDuration(startStr: string, endStr: string): string {
  const start = new Date(startStr);
  const end = new Date(endStr);
  const diffMs = end.getTime() - start.getTime();
  if (diffMs <= 0) return "";
  const hours = Math.floor(diffMs / 3600000);
  const minutes = Math.floor((diffMs % 3600000) / 60000);
  if (hours > 0 && minutes > 0)
    return `${hours}h${String(minutes).padStart(2, "0")}m`;
  if (hours > 0) return `${hours}h`;
  return `${minutes}m`;
}

function formatDateForNewApi(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDateForDisabledApi(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}.${month}.${day}`;
}

/**
 * Parse an Estonian-local datetime string (e.g. "2026-06-21T10:00")
 * and return a UTC ISO string.
 * Estonia is Europe/Tallinn (EET UTC+2 / EEST UTC+3).
 * We use the Intl API to derive the correct offset for the given date.
 */
function estonianToUtcIso(estonianStr: string): string | null {
  if (!estonianStr) return null;
  try {
    // Parse the parts from the string (expected: YYYY-MM-DDTHH:mm)
    const [datePart, timePart] = estonianStr.split("T");
    if (!datePart || !timePart) return null;
    const [year, month, day] = datePart.split("-").map(Number);
    const [hours, minutes] = timePart.split(":").map(Number);
    if ([year, month, day, hours, minutes].some((v) => isNaN(v))) return null;

    // Build a Date object in UTC first, then offset by the Estonian tz offset
    // We derive the offset by formatting a known date in Europe/Tallinn
    const tentativeUtc = new Date(
      Date.UTC(year, month - 1, day, hours, minutes, 0)
    );
    // Get what the UTC offset is for this datetime in Europe/Tallinn
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: "Europe/Tallinn",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    // Format tentativeUtc in Tallinn tz, parse back
    const parts: Record<string, string> = {};
    formatter.formatToParts(tentativeUtc).forEach((p) => {
      parts[p.type] = p.value;
    });
    const tallinnAtThatUtc = new Date(
      Date.UTC(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
        Number(parts.hour),
        Number(parts.minute),
        Number(parts.second || 0)
      )
    );
    const offsetMs = tallinnAtThatUtc.getTime() - tentativeUtc.getTime();
    // The actual UTC time = local Estonian time - offset
    const utcDate = new Date(tentativeUtc.getTime() - offsetMs);
    return utcDate.toISOString();
  } catch {
    return null;
  }
}

/**
 * Convert a UTC ISO string to an Estonian-local datetime string.
 * Output format: "YYYY-MM-DDTHH:mm" (always Estonian time, no timezone suffix).
 */
function utcIsoToEstonian(utcIso: string): string | null {
  if (!utcIso) return null;
  try {
    const date = new Date(utcIso);
    if (isNaN(date.getTime())) return null;
    // sv-SE locale produces "YYYY-MM-DD HH:mm:ss" which is easy to parse
    const formatter = new Intl.DateTimeFormat("sv-SE", {
      timeZone: "Europe/Tallinn",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const formatted = formatter.format(date); // "2026-06-21 10:00"
    const [datePart, timePart] = formatted.split(" ");
    if (!datePart || !timePart) return null;
    const timeShort = timePart.substring(0, 5); // "HH:mm"
    return `${datePart}T${timeShort}`;
  } catch {
    return null;
  }
}

/** Extract date part from an Estonian-local datetime string as a local Date. */
function estonianDatePart(estonianStr: string): Date | null {
  if (!estonianStr) return null;
  try {
    const [datePart] = estonianStr.split("T");
    const [year, month, day] = datePart.split("-").map(Number);
    if ([year, month, day].some((v) => isNaN(v))) return null;
    return new Date(year, month - 1, day);
  } catch {
    return null;
  }
}

/** Return the event-level overall max available (total capacity for the event). */
function getEventMaxAvailable(event: any): number | null {
  if (event.maxAvailable !== undefined && event.maxAvailable !== null)
    return event.maxAvailable;
  if (event.availableSeats !== undefined && event.availableSeats !== null)
    return event.availableSeats;
  if (event.capacity !== undefined && event.capacity !== null)
    return event.capacity;
  return null;
}

function getTicketmaxOrderQty(ticketProduct: any): number | undefined {
  return parseMaxOrderQty(ticketProduct?.maxOrderQty);
}

function getTicketEffectiveMin(
  ticketProduct: any,
  enforceFirstOrderMin: boolean
): number {
  return resolveEffectiveMinQty(
    ticketProduct?.minQty,
    ticketProduct?.minOrderQty,
    enforceFirstOrderMin
  );
}

/** Parse sales period offset: numeric milliseconds or numeric string. Returns null for invalid/missing. */
function parseOffsetMs(value: any): number | null {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "number") {
    return Number.isFinite(value) && value >= 0 ? value : null;
  }

  const normalized = String(value).trim();
  if (normalized === "") return null;

  const numericMs = Number(normalized);
  return Number.isFinite(numericMs) && numericMs >= 0 ? numericMs : null;
}

function isWithinSalesPeriod(
  eventStart: string,
  salesPeriodStartBeforeEventDatetime: any,
  salesPeriodEndBeforeEventDatetime: any
): boolean {
  const eventStartMs = new Date(eventStart).getTime();
  if (!Number.isFinite(eventStartMs)) return true;

  const startOffsetMs = parseOffsetMs(salesPeriodStartBeforeEventDatetime);
  const endOffsetMs = parseOffsetMs(salesPeriodEndBeforeEventDatetime);
  const now = Date.now();

  const salesStartMs =
    startOffsetMs === null ? Number.NEGATIVE_INFINITY : eventStartMs - startOffsetMs;
  // When end is missing, treat as "until the event" (sales close at event start)
  const salesEndMs =
    endOffsetMs === null ? eventStartMs : eventStartMs - endOffsetMs;

  // Invalid window config should not hide tickets unexpectedly.
  if (salesStartMs > salesEndMs) return true;
  return now >= salesStartMs && now <= salesEndMs;
}

function getVisibleTicketProducts(event: any): any[] {
  const products = event.ticketProducts || [];
  return products.filter((product: any) => {
    const start = product?.salesPeriodStartBeforeEventDatetime;
    const end = product?.salesPeriodEndBeforeEventDatetime;
    return isWithinSalesPeriod(event.start, start, end);
  });
}

function eventHasVisibleTickets(event: any): boolean {
  return getVisibleTicketProducts(event).length > 0;
}

function parseDisabledDayKey(value: any): string | null {
  const raw = String(value ?? "").trim();
  if (!raw) return null;

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;

  // Use local calendar day: timestamp converted to browser timezone.
  // e.g. "2026-03-19T22:00:00Z" in GMT+2 → 2026-03-20 00:00 local → disable March 20.

  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const day = d.getDate();
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

type FirstAvailableDatePayload = {
  forAll?: string | null;
  forGroup?: Record<string, string>;
};

type EventDatesPayload = {
  disabledEvents?: unknown[];
  firstAvailableDate?: FirstAvailableDatePayload | null;
};

function extractEventDatesPayload(raw: any): EventDatesPayload | null {
  const isPayload = (value: any): value is EventDatesPayload =>
    Boolean(
      value &&
        typeof value === "object" &&
        ("disabledEvents" in value || "firstAvailableDate" in value)
    );

  if (isPayload(raw)) return raw;
  if (isPayload(raw?.data)) return raw.data;
  if (isPayload(raw?.data?.data)) return raw.data.data;
  return null;
}

function parseCalendarDateFromApi(value: any): Date | null {
  const dayKey = parseDisabledDayKey(value);
  if (!dayKey) return null;
  const [year, month, day] = dayKey.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/** Find event by preferred UTC ISO start time */
function findPreferredEvent(events: any[], preferStart?: string): any {
  if (!preferStart || events.length === 0) return null;
  const preferMs = new Date(preferStart).getTime();
  return events.find((e: any) => new Date(e.start).getTime() === preferMs) ?? null;
}

type SelectionResult = {
  e1: any;
  e2: any;
  tripMode: "roundTrip" | "oneWay";
  group1Enabled: boolean;
  group2Enabled: boolean;
};

/**
 * Default event selection logic (when URL params absent or no match).
 * Priority: 1) round-trip, 2) one-way departure, 3) one-way return.
 */
function computeDefaultSelection(
  visible1: any[],
  visible2: any[]
): SelectionResult | null {
  // 1. Round-trip: first departure with valid same-day return
  for (const e1 of visible1) {
    const endTime = new Date(e1.end).getTime();
    const validE2 = visible2.find(
      (e: any) => new Date(e.start).getTime() >= endTime
    );
    if (validE2) {
      return {
        e1,
        e2: validE2,
        tripMode: "roundTrip",
        group1Enabled: true,
        group2Enabled: true,
      };
    }
  }
  // 2. One-way departure: first available
  if (visible1.length > 0) {
    return {
      e1: visible1[0],
      e2: null,
      tripMode: "oneWay",
      group1Enabled: true,
      group2Enabled: false,
    };
  }
  // 3. One-way return: first available
  if (visible2.length > 0) {
    return {
      e1: null,
      e2: visible2[0],
      tripMode: "oneWay",
      group1Enabled: false,
      group2Enabled: true,
    };
  }
  return null;
}

/**
 * Try URL params first, then default selection.
 * Returns selection result or null if nothing found.
 */
function computeSelection(
  visible1: any[],
  visible2: any[],
  preferStart1?: string,
  preferStart2?: string
): SelectionResult | null {
  const hasUrlParams = Boolean(preferStart1 || preferStart2);

  if (hasUrlParams) {
    // Depart only (one-way)
    if (preferStart1 && !preferStart2) {
      const pref1 = findPreferredEvent(visible1, preferStart1);
      if (pref1) {
        return {
          e1: pref1,
          e2: null,
          tripMode: "oneWay",
          group1Enabled: true,
          group2Enabled: false,
        };
      }
    }
    // Return only (one-way)
    if (preferStart2 && !preferStart1) {
      const pref2 = findPreferredEvent(visible2, preferStart2);
      if (pref2) {
        return {
          e1: null,
          e2: pref2,
          tripMode: "oneWay",
          group1Enabled: false,
          group2Enabled: true,
        };
      }
    }
    // Both (round-trip)
    if (preferStart1 && preferStart2) {
      const pref1 = findPreferredEvent(visible1, preferStart1);
      if (pref1) {
        const endTime = new Date(pref1.end).getTime();
        const pref2 = findPreferredEvent(visible2, preferStart2);
        const validE2 =
          pref2 && new Date(pref2.start).getTime() >= endTime
            ? pref2
            : visible2.find(
                (e: any) => new Date(e.start).getTime() >= endTime
              );
        if (validE2) {
          return {
            e1: pref1,
            e2: validE2,
            tripMode: "roundTrip",
            group1Enabled: true,
            group2Enabled: true,
          };
        }
      }
    }
  }

  return computeDefaultSelection(visible1, visible2);
}

// ── Spinner ─────────────────────────────────────────────────────────────

const SimpleSpinner = (): JSX.Element => (
  <BootstrapSpinner animation="border" role="status" as="div" />
);

// ── Translations ────────────────────────────────────────────────────────

const translations: Record<string, any> = {
  et: {
    round_trip: "Edasi-tagasi",
    one_way: "Üks suund",
    loading: "Laen sündmusi...",
    no_events: "Sellel kuupäeval ei ole sündmusi",
    select: "Vali",
    selected: "Valitud",
    free_seats: "Saadaval",
    available: "Saadaval",
    sold_out: "Välja müüdud!",
    loading_info: "Laen sündmust...",
    error: "Vabade kohtadega sündmuseid ei leitud",
  },
  en: {
    round_trip: "Round trip",
    one_way: "One way",
    loading: "Loading events...",
    no_events: "No events available on this date",
    select: "Select",
    selected: "Selected",
    free_seats: "Available",
    available: "Available",
    sold_out: "Sold out!",
    loading_info: "Loading event information...",
    error: "No available events found",
  },
};

// ═══════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════

export default function EventRoundTripComponent({
  id,
  uuid,
  attributes,
  childElements,
  childWebpageComponentRelations,
}: EventRoundTripComponent_) {
  const editorId = useAppSelector((state) => state.textEditor?.get("id"));
  const component = (useAppSelector((state) =>
    getComponentFromStructureMap(state.builder.structure.components, uuid ?? id)
  ) as EventRoundTripComponent_) ?? ({
    id: typeof id === "number" ? id : -1,
    parent_id: null,
    childElements: childElements ?? [],
    childWebpageComponentRelations: childWebpageComponentRelations ?? [],
    sort_order: 0,
    type: "eventRoundTripComponent",
    uuid: uuid ?? null,
    version: 1,
    webpage_id: 0,
    attributes,
  } as EventRoundTripComponent_);

  const isBuilder = false;

  const {
    increaseCartQuantity,
    decreaseCartQuantity,
    removeFromCart,
    setCartQuantity,
    cartItems,
    isFirstOrderMinQtyEnforced,
    registerEventSellOrderFlags,
    order,
  } = useShoppingCart();

  const navigate = useNavigate();
  const location = useLocation();
  const locale = usePageLanguage();
  const t = translations[locale] || translations.et;

  // ── Parse URL params (depart / return) ─────────────────────────────

  const urlParams = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const depart = sp.get("depart") || ""; // Estonian local time
    const ret = sp.get("return") || ""; // Estonian local time
    return { depart, return: ret };
  }, [location.search]);

  // ── Core state ──────────────────────────────────────────────────────

  const today = new Date();

  // Determine initial date from URL (or today)
  const initialDate = useMemo(() => {
    if (urlParams.depart) {
      const d = estonianDatePart(urlParams.depart);
      if (d) return d;
    }
    return today;
  }, []); // only on mount

  const [selectedDate, setSelectedDate] = useState<Date>(initialDate);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stylesLoaded, setStylesLoaded] = useState(false);

  // Trip mode
  const [tripMode, setTripMode] = useState<"roundTrip" | "oneWay">("roundTrip");

  // Group enable (clicking headers)
  const [group1Enabled, setGroup1Enabled] = useState(true);
  const [group2Enabled, setGroup2Enabled] = useState(true);

  // Events for each group
  const [group1Events, setGroup1Events] = useState<any[]>([]);
  const [group2Events, setGroup2Events] = useState<any[]>([]);
  const [loadingGroup1, setLoadingGroup1] = useState(false);
  const [loadingGroup2, setLoadingGroup2] = useState(false);

  // Selected events (one per group)
  const [selectedEvent1, setSelectedEvent1] = useState<any>(null);
  const [selectedEvent2, setSelectedEvent2] = useState<any>(null);

  // Disabled dates (uses group 2)
  const [disabledDates, setDisabledDates] = useState<Date[]>([]);
  const disabledDatesRequestIdRef = useRef(0);
  const disabledDatesCacheRef = useRef<Map<string, Set<string>>>(new Map());
  const MAX_CACHED_DISABLED_RANGES = 5;
  const disabledDateTimeSet = useMemo(
    () => new Set(disabledDates.map((d) => d.getTime())),
    [disabledDates]
  );

  // Events with at least one in-period ticket (used for availability/selection)
  const group1VisibleEvents = useMemo(
    () => group1Events.filter((e: any) => eventHasVisibleTickets(e)),
    [group1Events]
  );
  const group2VisibleEvents = useMemo(
    () => group2Events.filter((e: any) => eventHasVisibleTickets(e)),
    [group2Events]
  );

  // Group hashes & names (from attributes)
  const groupHash1 = component.attributes?.event_group_hash_1;
  const groupHash2 = component.attributes?.event_group_hash_2;
  const groupName1 = component.attributes?.event_group_name_1 || "Group 1";
  const groupName2 = component.attributes?.event_group_name_2 || "Group 2";

  // Tooltip state
  const [tooltip, setTooltip] = useState<{
    html: string;
    top: number;
    left: number;
    anchorEl: HTMLElement;
    anchor: "top";
    pinned: boolean; // true when opened by tap/click (mobile), false when hover only
    positionReady?: boolean; // true after Floating UI has computed position
  } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const calcTooltip = useCallback(
    (
      el: HTMLElement,
      html: string,
      pinned: boolean
    ): NonNullable<typeof tooltip> => {
      const rect = el.getBoundingClientRect();
      return {
        html,
        top: rect.top,
        left: rect.left + rect.width / 2,
        anchorEl: el,
        anchor: "top",
        pinned,
        positionReady: false,
      };
    },
    []
  );

  // Position tooltip with Floating UI (flip/shift when near viewport edges)
  useEffect(() => {
    if (!tooltip || !tooltipRef.current) return;
    const anchorEl = tooltip.anchorEl;
    const floatingEl = tooltipRef.current;

    const updatePosition = () => {
      computePosition(anchorEl, floatingEl, {
        placement: "right",
        strategy: "fixed",
        middleware: [offset(8), flip(), shift({ padding: 8 })],
      }).then(({ x, y }) => {
        setTooltip((prev) =>
          prev ? { ...prev, top: y, left: x, positionReady: true } : null
        );
      });
    };

    updatePosition();
    const cleanup = autoUpdate(anchorEl, floatingEl, updatePosition);
    return cleanup;
  }, [tooltip?.anchorEl, tooltip?.html, tooltip?.pinned]);

  // Close pinned tooltip when clicking/tapping outside
  useEffect(() => {
    if (!tooltip?.pinned) return;
    const close = () => setTooltip(null);
    document.addEventListener("click", close);
    document.addEventListener("touchstart", close);
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("touchstart", close);
    };
  }, [tooltip?.pinned]);

  // ── Helper: get cart quantity ──────────────────────────────────────

  const getEventRef = (event: any) => event?.hash;

  const getCartQty = useCallback(
    (ticketProduct: any, eventRef: string): number => {
      const productUuid = resolveEventProductUuid(ticketProduct);
      return getEventCartLineQuantity(
        cartItems,
        eventCartMutationTarget({
          productUuid: productUuid ?? "",
          eventRef,
        })
      );
    },
    [cartItems]
  );

  /** Total ticket quantity in the cart for a given event (excludes extras and additional services). */
  const getEventTicketCartQty = useCallback(
    (eventRef: string, event: { ticketProducts?: Array<{ productUuid?: unknown }> }): number => {
      return getEventTicketCartQuantity(
        cartItems,
        eventRef,
        collectEventTicketProductUuids(event),
      );
    },
    [cartItems]
  );

  /**
   * Compute the effective maximum quantity that can be set for a
   * specific variation product within an event, taking into account:
   *   1. The individual product's maxAvailable
   *   2. The event-level maxAvailable (overall cap across all products)
   *   3. How many tickets from other products are already in the cart for
   *      this event.
   */
  const getEffectiveMax = useCallback(
    (productMaxAvailable: number, event: any, ticketProduct: any): number => {
      const productCap = resolveDisplayAvailable(
        productMaxAvailable,
        ticketProduct?.maxOrderQty,
      );
      const eventMax = getEventMaxAvailable(event);
      if (eventMax === null) {
        return productCap;
      }

      const eventRef = getEventRef(event);
      const totalInCart = getEventTicketCartQty(eventRef, event);
      const currentProductQty = getCartQty(ticketProduct, eventRef);
      const eventRemaining = eventMax - totalInCart + currentProductQty;

      return Math.max(0, Math.min(productCap, eventRemaining));
    },
    [getEventTicketCartQty, getCartQty],
  );

  const getTicketProductData = useCallback((product: any) => {
    const productMax = product?.maxAvailable ?? 0;
    const displayAvailable = resolveDisplayAvailable(
      productMax,
      product?.maxOrderQty,
    );
    const salesPeriodStartBeforeEventDatetime =
      product?.salesPeriodStartBeforeEventDatetime;
    const salesPeriodEndBeforeEventDatetime =
      product?.salesPeriodEndBeforeEventDatetime;

    return {
      productMax,
      displayAvailable,
      salesPeriodStartBeforeEventDatetime,
      salesPeriodEndBeforeEventDatetime,
    };
  }, []);

  const isTicketProductInSalesPeriod = useCallback(
    (event: any, product: any): boolean => {
      const {
        salesPeriodStartBeforeEventDatetime,
        salesPeriodEndBeforeEventDatetime,
      } = getTicketProductData(product);
      return isWithinSalesPeriod(
        event.start,
        salesPeriodStartBeforeEventDatetime,
        salesPeriodEndBeforeEventDatetime
      );
    },
    [getTicketProductData]
  );

  // ── Group & cart migration helpers ─────────────────────────────────

  /** Get all event refs (hash or id) for a specific group. */
  const getGroupEventRefs = useCallback(
    (groupNum: 1 | 2): string[] => {
      const events = groupNum === 1 ? group1Events : group2Events;
      return events.map((e: any) => getEventRef(e));
    },
    [group1Events, group2Events]
  );

  /** Clear all cart items that belong to events in a specific group. */
  const clearCartForGroup = useCallback(
    (groupNum: 1 | 2) => {
      const eventRefs = new Set(getGroupEventRefs(groupNum));
      const itemsToClear = cartItems.filter((ci) => {
        const ref = ci.eventHash
        return ref !== undefined && eventRefs.has(ref);
      });
      for (const item of itemsToClear) {
        removeFromCart(item.productUuid ?? "", item.eventHash);
      }
    },
    [cartItems, getGroupEventRefs, removeFromCart]
  );

  /** Clear cart items for a single event. */
  const clearCartForEvent = useCallback(
    (eventRef: string) => {
      const items = cartItems.filter((ci) => {
        if (typeof eventRef === "string") return ci.eventHash === eventRef;
        return false;
      });
      for (const item of items) {
        removeFromCart(item.productUuid ?? "", item.eventHash);
      }
    },
    [cartItems, removeFromCart]
  );

  /**
   * Move cart items from source events to a target event.
   * Matches by productId. Respects BOTH the individual product maxAvailable
   * AND the overall event-level maxAvailable (cumulative across all products).
   * Non-matching items are simply removed.
   */
  const moveCartToEvent = useCallback(
    (fromEventRefs: string[], toEvent: any) => {
      if (!toEvent) return;
      const fromSet = new Set(fromEventRefs);
      const itemsToMove = cartItems.filter((ci) => {
        const ref = ci.eventHash;
        return ref !== undefined && fromSet.has(ref);
      });
      if (itemsToMove.length === 0) return;

      const targetTickets: any[] = toEvent.ticketProducts || [];
      const toEventRef = getEventRef(toEvent);

      // Track how many tickets have been added to the target event so far
      // so we never exceed the event-level cap.
      const eventMax = getEventMaxAvailable(toEvent) ?? Infinity;
      let cumulativeAdded = 0;

      // First: remove ALL old items
      for (const item of itemsToMove) {
        removeFromCart(item.productUuid ?? "", item.eventHash);
      }

      // Then: add matching products to the target event
      for (const item of itemsToMove) {
        const itemProductUuid = resolveEventProductUuid(item);
        if (!itemProductUuid) continue;
        const matchingTicket = targetTickets.find(
          (t: any) => resolveEventProductUuid(t) === itemProductUuid
        );
        if (!matchingTicket) continue; // No match — ticket dropped
        if (!isTicketProductInSalesPeriod(toEvent, matchingTicket)) continue;

        const { productMax, salesPeriodEndBeforeEventDatetime } =
          getTicketProductData(matchingTicket);
        const eventRemaining = eventMax - cumulativeAdded;
        const perOrderCap =
          getTicketmaxOrderQty(matchingTicket) ?? Number.POSITIVE_INFINITY;
        const cap = Math.min(productMax, eventRemaining, perOrderCap);
        const enforceFirstOrderMin = isFirstOrderMinQtyEnforced(toEventRef);
        const effectiveMin = getTicketEffectiveMin(
          matchingTicket,
          enforceFirstOrderMin
        );
        const minForClamp = effectiveMin > 1 ? effectiveMin : undefined;
        const qty = clampQuantityToMinAndCap(
          item.quantity,
          minForClamp,
          cap
        );
        if (qty <= 0) continue;

        const ticketProductUuid = resolveEventProductUuid(matchingTicket);
        const start = new Date(toEvent.start).toISOString();
        increaseCartQuantity(
          ticketProductUuid ?? "",
          toEventRef,
          start,
          true,
          salesPeriodEndBeforeEventDatetime,
          productMax,
          getTicketmaxOrderQty(matchingTicket),
          matchingTicket?.minOrderQty,
          matchingTicket?.minQty,
          toEvent.hasActiveSellOrders,
        );
        if (qty > 1) {
          setCartQuantity(
            qty,
            ticketProductUuid ?? "",
            toEventRef
          );
        }
        cumulativeAdded += qty;
      }
    },
    [
      cartItems,
      removeFromCart,
      increaseCartQuantity,
      setCartQuantity,
      isTicketProductInSalesPeriod,
      getTicketProductData,
      isFirstOrderMinQtyEnforced,
    ]
  );

  // ── Cart actions ──────────────────────────────────────────────────

  const handleIncrease = useCallback(
    (ticketProduct: any, event: any) => {
      if (!isTicketProductInSalesPeriod(event, ticketProduct)) return;

      const eventRef = getEventRef(event);
      const enforceFirstOrderMin = isFirstOrderMinQtyEnforced(eventRef);
      const effectiveMin = getTicketEffectiveMin(
        ticketProduct,
        enforceFirstOrderMin
      );
      const minForUi = effectiveMin > 1 ? effectiveMin : undefined;
      // Find the product-level maxAvailable
      const { productMax, salesPeriodEndBeforeEventDatetime } =
        getTicketProductData(ticketProduct);
      const effectiveMax = getEffectiveMax(
        productMax,
        event,
        ticketProduct
      );
      if (isEffectivelySoldOut(minForUi, effectiveMax)) return;
      const currentQty = getCartQty(ticketProduct, eventRef);

      // Don't exceed either limit
      if (currentQty >= effectiveMax) return;

      const ticketProductUuid = resolveEventProductUuid(ticketProduct);
      const start = new Date(event.start).toISOString();
      if (
        currentQty === 0 &&
        hasMinOrderQtyConstraint(minForUi)
      ) {
        const startQty = resolveStartQuantity(
          minForUi,
          effectiveMax
        );
        increaseCartQuantity(
          ticketProductUuid ?? "",
          eventRef,
          start,
          true,
          salesPeriodEndBeforeEventDatetime,
          productMax,
          getTicketmaxOrderQty(ticketProduct),
          ticketProduct.minOrderQty,
          ticketProduct.minQty,
          event.hasActiveSellOrders,
        );
        if (startQty > 1) {
          setCartQuantity(
            startQty,
            ticketProductUuid ?? "",
            eventRef
          );
        }
      } else {
        increaseCartQuantity(
          ticketProductUuid ?? "",
          eventRef,
          start,
          true,
          salesPeriodEndBeforeEventDatetime,
          productMax,
          getTicketmaxOrderQty(ticketProduct),
          ticketProduct?.minOrderQty,
          ticketProduct?.minQty,
          event.hasActiveSellOrders,
        );
      }
    },
    [
      increaseCartQuantity,
      setCartQuantity,
      getEffectiveMax,
      getCartQty,
      getEventRef,
      isTicketProductInSalesPeriod,
      getTicketProductData,
      isFirstOrderMinQtyEnforced,
    ]
  );

  const handleDecrease = useCallback(
    (ticketProduct: any, event: any) => {
      const eventRef = getEventRef(event);
      const currentQty = getCartQty(ticketProduct, eventRef);
      const ticketProductUuid = resolveEventProductUuid(ticketProduct);
      const minQty = getTicketEffectiveMin(
        ticketProduct,
        isFirstOrderMinQtyEnforced(eventRef)
      );
      if (currentQty <= minQty) {
        removeFromCart(ticketProductUuid ?? "", eventRef);
      } else {
        decreaseCartQuantity(ticketProductUuid ?? "", eventRef);
      }
    },
    [getCartQty, getEventRef, decreaseCartQuantity, removeFromCart, isFirstOrderMinQtyEnforced]
  );

  // ── Local input value tracking (allows clearing the field) ────────

  // Key = "productRef-eventRef", value = string typed by user (may be empty)
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  // Mirror inputValues to a ref so blur handlers always see the latest value
  const inputValuesRef = React.useRef(inputValues);
  inputValuesRef.current = inputValues;

  const getInputKey = (ticketProduct: any, eventRef: string) =>
    getEventCartInputKey(
      resolveEventProductUuid(ticketProduct) ?? "missing-product-uuid",
      eventRef ?? ""
    );

  const handleQtyInputChange = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>,
      ticketProduct: any,
      event: any,
      effectiveMax: number
    ) => {
      const eventRef = getEventRef(event);
      const enforceFirstOrderMin = isFirstOrderMinQtyEnforced(eventRef);
      const effectiveMin = getTicketEffectiveMin(
        ticketProduct,
        enforceFirstOrderMin
      );
      const minForUi = effectiveMin > 1 ? effectiveMin : undefined;
      const key = getInputKey(ticketProduct, eventRef);
      const ticketProductUuid = resolveEventProductUuid(ticketProduct);
      const rawValue = e.target.value;

      // Allow the user to empty the field while typing
      if (rawValue === "") {
        setInputValues((prev) => ({ ...prev, [key]: "" }));
        return;
      }

      // Only allow digits
      if (!/^\d+$/.test(rawValue)) return;

      let newQty = parseInt(rawValue, 10);
      if (isNaN(newQty)) return;
      if (newQty < 0) newQty = 0;
      newQty = clampQuantityToMinAndCap(newQty, minForUi, effectiveMax);

      // Update local display value
      setInputValues((prev) => ({ ...prev, [key]: String(newQty) }));

      const currentQty = getCartQty(ticketProduct, eventRef);
      if (
        !isTicketProductInSalesPeriod(event, ticketProduct) &&
        newQty >= currentQty
      ) {
        setInputValues((prev) => ({ ...prev, [key]: String(currentQty) }));
        return;
      }
      if (newQty === currentQty) return;

      if (newQty === 0) {
        removeFromCart(
          ticketProductUuid ?? "",
          eventRef
        );
      } else if (currentQty === 0 && newQty > 0) {
        // Item not yet in cart — add, then set qty
        const { productMax, salesPeriodEndBeforeEventDatetime } =
          getTicketProductData(ticketProduct);
        const start = new Date(event.start).toISOString();
        increaseCartQuantity(
          ticketProductUuid ?? "",
          eventRef,
          start,
          true,
          salesPeriodEndBeforeEventDatetime,
          productMax,
          getTicketmaxOrderQty(ticketProduct),
          ticketProduct?.minOrderQty,
          ticketProduct?.minQty,
          event.hasActiveSellOrders,
        );
        if (newQty > 1) {
          setCartQuantity(
            newQty,
            ticketProductUuid ?? "",
            eventRef
          );
        }
      } else {
        setCartQuantity(
          newQty,
          ticketProductUuid ?? "",
          eventRef
        );
      }
    },
    [
      getCartQty,
      getEventRef,
      removeFromCart,
      increaseCartQuantity,
      setCartQuantity,
      isTicketProductInSalesPeriod,
      getTicketProductData,
      isFirstOrderMinQtyEnforced,
    ]
  );

  /** When input loses focus, commit the value (treat empty as 0). */
  const handleQtyInputBlur = useCallback(
    (ticketProduct: any, event: any) => {
      const eventRef = getEventRef(event);
      const key = getInputKey(ticketProduct, eventRef);
      const ticketProductUuid = resolveEventProductUuid(ticketProduct);
      // Read from ref to always get the freshest value (avoids stale closure)
      const localVal = inputValuesRef.current[key];

      // Clear local tracking — let the displayed value sync from cart
      setInputValues((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });

      // Only treat as "set to 0" if the user actually emptied the field
      // (localVal === ""). If localVal is undefined the user just focused
      // and blurred without typing — do nothing.
      if (localVal === "") {
        const currentQty = getCartQty(ticketProduct, eventRef);
        if (currentQty > 0) {
          removeFromCart(
            ticketProductUuid ?? "",
            eventRef
          );
        }
      }
    },
    [getCartQty, getEventRef, removeFromCart]
  );

  // ── Fetch events for a date and group ─────────────────────────────

  const fetchEventsForGroup = useCallback(
    async (date: Date, groupHash: string): Promise<any[]> => {
      const dateStr = formatDateForNewApi(date);
      let events: any[] = [];

      if (isBuilder) {
        const resp = await fetchApiGet<any>(
          `/events?date=${encodeURIComponent(
            dateStr
          )}&groupHash=${groupHash}&perPage=100&sortBy=start&direction=asc`
        );
        if (Array.isArray(resp)) events = resp;
        else if (resp?.data && Array.isArray(resp.data)) events = resp.data;
      } else {
        const resp = await cachedAxiosGet(
          `events?date=${encodeURIComponent(
            dateStr
          )}&groupHash=${groupHash}&perPage=100&sortBy=start&direction=asc`
        );
        if (resp.data?.data) events = resp.data.data;
        else if (Array.isArray(resp.data)) events = resp.data;
      }
      return events;
    },
    [isBuilder]
  );

  // ── Fetch both groups on date change ──────────────────────────────

  /**
   * @param date                The calendar date to fetch
   * @param migrateCart         If true, move cart items from old selected events to new ones
   * @param preferStart1        (optional) UTC ISO start time to prefer for Group 1 event
   * @param preferStart2        (optional) UTC ISO start time to prefer for Group 2 event
   * @param migrateExternalCart If true, migrate cart items from events NOT in the loaded
   *                            set (e.g. items left over from a previous date when arriving
   *                            via URL) to the newly selected events.
   * @param preserveDirection   If true (day change), keep currently active one-way direction.
   */
  const fetchAllEvents = useCallback(
    async (
      date: Date,
      migrateCart: boolean = false,
      preferStart1?: string,
      preferStart2?: string,
      migrateExternalCart: boolean = false,
      preserveDirection: boolean = false
    ): Promise<SelectionResult | null> => {
      if (!groupHash1 && !groupHash2) return null;

      // Snapshot current selected events BEFORE fetching (for cart migration)
      const prevEvent1 = selectedEvent1;
      const prevEvent2 = selectedEvent2;
      const prevGroup1Events = group1Events;
      const prevGroup2Events = group2Events;

      let events1: any[] = [];
      let events2: any[] = [];

      if (groupHash1) {
        setLoadingGroup1(true);
        try {
          events1 = await fetchEventsForGroup(date, groupHash1);
          setGroup1Events(events1);
        } catch (err) {
          console.error("Error fetching group 1 events:", err);
          setGroup1Events([]);
        }
        setLoadingGroup1(false);
      }

      if (groupHash2) {
        setLoadingGroup2(true);
        try {
          events2 = await fetchEventsForGroup(date, groupHash2);
          setGroup2Events(events2);
        } catch (err) {
          console.error("Error fetching group 2 events:", err);
          setGroup2Events([]);
        }
        setLoadingGroup2(false);
      }

      registerEventSellOrderFlags([...events1, ...events2]);

      // --- Auto-select from visible events only (events with in-period tickets) ---
      const visible1 = events1.filter((e: any) => eventHasVisibleTickets(e));
      const visible2 = events2.filter((e: any) => eventHasVisibleTickets(e));

      let result = computeSelection(
        visible1,
        visible2,
        preferStart1,
        preferStart2
      );

      const hasExplicitPreference = Boolean(preferStart1 || preferStart2);
      if (
        preserveDirection &&
        !hasExplicitPreference &&
        tripMode === "oneWay" &&
        result
      ) {
        const matchesEvent = (candidate: any, selected: any): boolean => {
          if (!candidate || !selected) return false;
          return getEventRef(candidate) === getEventRef(selected);
        };
        const keepGroup1 = group1Enabled && !group2Enabled;
        const keepGroup2 = group2Enabled && !group1Enabled;

        if (keepGroup1) {
          const retainedSelection =
            selectedEvent1 &&
            visible1.find((event) => matchesEvent(event, selectedEvent1));
          result = {
            e1: retainedSelection ?? visible1[0] ?? null,
            e2: null,
            tripMode: "oneWay",
            group1Enabled: true,
            group2Enabled: false,
          };
        } else if (keepGroup2) {
          const retainedSelection =
            selectedEvent2 &&
            visible2.find((event) => matchesEvent(event, selectedEvent2));
          result = {
            e1: null,
            e2: retainedSelection ?? visible2[0] ?? null,
            tripMode: "oneWay",
            group1Enabled: false,
            group2Enabled: true,
          };
        }
      }

      // Keep round-trip mode sticky unless the user explicitly changes mode.
      // This prevents date/event auto-selection from silently switching to one-way.
      if (result && tripMode === "roundTrip") {
        result = {
          ...result,
          tripMode: "roundTrip",
          group1Enabled: true,
          group2Enabled: true,
        };
      }

      const newE1 = result?.e1 ?? null;
      const newE2 = result?.e2 ?? null;

      setSelectedEvent1(newE1);
      setSelectedEvent2(newE2);

      if (result) {
        setTripMode(result.tripMode);
        setGroup1Enabled(result.group1Enabled);
        setGroup2Enabled(result.group2Enabled);
        if (result.tripMode === "oneWay") {
          if (!result.group2Enabled) clearCartForGroup(2);
          if (!result.group1Enabled) clearCartForGroup(1);
        }
      }

      // --- Cart migration: move items from old events to new events ---
      if (migrateCart) {
        const oldGroup1Refs = prevGroup1Events.map((e: any) => getEventRef(e));
        const oldGroup2Refs = prevGroup2Events.map((e: any) => getEventRef(e));

        if (newE1 && oldGroup1Refs.length > 0) {
          const newE1Ref = getEventRef(newE1);
          const fromRefs = oldGroup1Refs.filter((ref) => ref !== newE1Ref);
          if (fromRefs.length > 0) moveCartToEvent(fromRefs, newE1);
        } else if (oldGroup1Refs.length > 0) {
          for (const ref of oldGroup1Refs) {
            const items = cartItems.filter(
              (ci) => ci.eventHash === ref
            );
            for (const item of items)
              removeFromCart(item.productUuid ?? "", item.eventHash);
          }
        }

        if (newE2 && oldGroup2Refs.length > 0) {
          const newE2Ref = getEventRef(newE2);
          const fromRefs = oldGroup2Refs.filter((ref) => ref !== newE2Ref);
          if (fromRefs.length > 0) moveCartToEvent(fromRefs, newE2);
        } else if (oldGroup2Refs.length > 0) {
          for (const ref of oldGroup2Refs) {
            const items = cartItems.filter(
              (ci) => ci.eventHash === ref
            );
            for (const item of items)
              removeFromCart(item.productUuid ?? "", item.eventHash);
          }
        }
      }

      // --- URL/external cart migration ---
      // When arriving via URL to a different date, cart items may exist for
      // events that are not part of today's loaded events. Move them to the
      // newly selected events, matching by productId (same logic as moveCartToEvent
      // but split across two target events instead of one).
      if (migrateExternalCart && (newE1 || newE2)) {
        const loadedRefs = new Set<string | number>([
          ...events1.map((e: any) => getEventRef(e)),
          ...events2.map((e: any) => getEventRef(e)),
        ]);

        const externalItems = cartItems.filter((ci) => {
          const ref = ci.eventHash;
          return ref !== undefined && !loadedRefs.has(ref);
        });

        if (externalItems.length > 0) {
          for (const item of externalItems) {
            removeFromCart(item.productUuid ?? "", item.eventHash);
          }

          const tickets1: any[] = newE1?.ticketProducts || [];
          const tickets2: any[] = newE2?.ticketProducts || [];
          const eventMax1 = newE1 ? getEventMaxAvailable(newE1) ?? Infinity : 0;
          const eventMax2 = newE2 ? getEventMaxAvailable(newE2) ?? Infinity : 0;
          let added1 = 0;
          let added2 = 0;
          const newE1Ref = newE1 ? getEventRef(newE1) : null;
          const newE2Ref = newE2 ? getEventRef(newE2) : null;

          for (const item of externalItems) {
            const itemProductUuid = resolveEventProductUuid(item);
            if (!itemProductUuid) continue;
            const match1 = tickets1.find(
              (t: any) => resolveEventProductUuid(t) === itemProductUuid
            );
            if (match1 && newE1 && newE1Ref) {
              if (!isTicketProductInSalesPeriod(newE1, match1)) continue;
              const { productMax, salesPeriodEndBeforeEventDatetime } =
                getTicketProductData(match1);
              const perOrderCap1 =
                getTicketmaxOrderQty(match1) ?? Number.POSITIVE_INFINITY;
              const cap1 = Math.min(productMax, eventMax1 - added1, perOrderCap1);
              const enforceFirstOrderMin1 = isFirstOrderMinQtyEnforced(newE1Ref);
              const effectiveMin1 = getTicketEffectiveMin(
                match1,
                enforceFirstOrderMin1
              );
              const minForClamp1 = effectiveMin1 > 1 ? effectiveMin1 : undefined;
              const qty = clampQuantityToMinAndCap(
                item.quantity,
                minForClamp1,
                cap1
              );
              if (qty > 0) {
                const ticketProductUuid = resolveEventProductUuid(match1);
                increaseCartQuantity(
                  ticketProductUuid ?? "",
                  newE1Ref,
                  new Date(newE1.start).toISOString(),
                  true,
                  salesPeriodEndBeforeEventDatetime,
                  productMax,
                  getTicketmaxOrderQty(match1),
                  match1?.minOrderQty,
                  match1?.minQty,
                  newE1.hasActiveSellOrders,
                );
                if (qty > 1) {
                  setCartQuantity(
                    qty,
                    ticketProductUuid ?? "",
                    newE1Ref
                  );
                }
                added1 += qty;
              }
              continue;
            }

            const match2 = tickets2.find(
              (t: any) => resolveEventProductUuid(t) === itemProductUuid
            );
            if (match2 && newE2 && newE2Ref) {
              if (!isTicketProductInSalesPeriod(newE2, match2)) continue;
              const { productMax, salesPeriodEndBeforeEventDatetime } =
                getTicketProductData(match2);
              const perOrderCap2 =
                getTicketmaxOrderQty(match2) ?? Number.POSITIVE_INFINITY;
              const cap2 = Math.min(productMax, eventMax2 - added2, perOrderCap2);
              const enforceFirstOrderMin2 = isFirstOrderMinQtyEnforced(newE2Ref);
              const effectiveMin2 = getTicketEffectiveMin(
                match2,
                enforceFirstOrderMin2
              );
              const minForClamp2 = effectiveMin2 > 1 ? effectiveMin2 : undefined;
              const qty = clampQuantityToMinAndCap(
                item.quantity,
                minForClamp2,
                cap2
              );
              if (qty > 0) {
                const ticketProductUuid = resolveEventProductUuid(match2);
                increaseCartQuantity(
                  ticketProductUuid ?? "",
                  newE2Ref,
                  new Date(newE2.start).toISOString(),
                  true,
                  salesPeriodEndBeforeEventDatetime,
                  productMax,
                  getTicketmaxOrderQty(match2),
                  match2?.minOrderQty,
                  match2?.minQty,
                  newE2.hasActiveSellOrders,
                );
                if (qty > 1) {
                  setCartQuantity(
                    qty,
                    ticketProductUuid ?? "",
                    newE2Ref
                  );
                }
                added2 += qty;
              }
            }
          }
        }
      }

      return result;
    },
    [
      groupHash1,
      groupHash2,
      fetchEventsForGroup,
      selectedEvent1,
      selectedEvent2,
      group1Events,
      group2Events,
      cartItems,
      tripMode,
      group1Enabled,
      group2Enabled,
      getEventRef,
      moveCartToEvent,
      removeFromCart,
      increaseCartQuantity,
      setCartQuantity,
      isTicketProductInSalesPeriod,
      getTicketProductData,
      clearCartForGroup,
      isFirstOrderMinQtyEnforced,
    ]
  );

  // ── Fetch disabled dates (uses group 2) ───────────────────────────

  const fetchDisabledDates = useCallback(
    async (
      startDate: Date,
      endDate: Date,
      options: {
        migrateExternalCart?: boolean;
        skipEventFetch?: boolean;
      } = {}
    ) => {
      const { migrateExternalCart = false, skipEventFetch = false } = options;
      const requestId = ++disabledDatesRequestIdRef.current;

      if (!groupHash1 && !groupHash2) return;

      try {
        const startF = formatDateForDisabledApi(startDate);
        const endF = formatDateForDisabledApi(endDate);

        const groupHashValue = [groupHash1, groupHash2]
          .filter(Boolean)
          .join(",");

        let payload: EventDatesPayload | null = null;

        if (isBuilder) {
          const resp = await fetchApiGet<any>(
            `/event-dates?start=${startF}&end=${endF}&groupHash=${groupHashValue}`
          );
          payload = extractEventDatesPayload(resp);
        } else {
          const resp = await cachedAxiosGet(
            `event-dates?start=${startF}&end=${endF}&groupHash=${groupHashValue}`
          );
          payload = extractEventDatesPayload(resp);
        }

        const isLatestDisabledDatesRequest =
          requestId === disabledDatesRequestIdRef.current;

        const disabledEvents =
          payload != null && Array.isArray(payload.disabledEvents)
            ? payload.disabledEvents
            : [];
        const firstAvailableForAll = payload?.firstAvailableDate?.forAll ?? null;

        const disabledDayKeys = new Set(
          disabledEvents
            .map((ds) => parseDisabledDayKey(ds))
            .filter((k): k is string => Boolean(k))
        );
        if (isLatestDisabledDatesRequest) {
          const rangeKey = `${startF}_${endF}`;
          disabledDatesCacheRef.current.set(rangeKey, disabledDayKeys);
          const cache = disabledDatesCacheRef.current;
          if (cache.size > MAX_CACHED_DISABLED_RANGES) {
            const firstKey = cache.keys().next().value;
            if (firstKey !== undefined) cache.delete(firstKey);
          }
          const merged = new Set<string>();
          cache.forEach((set) => set.forEach((k) => merged.add(k)));
          const parsed = Array.from(merged).map((key) => {
            const [year, month, day] = key.split("-").map(Number);
            return new Date(year, month - 1, day);
          });
          setDisabledDates(parsed);
        }

        if (skipEventFetch) return;

        const initMid = new Date(
          initialDate.getFullYear(),
          initialDate.getMonth(),
          initialDate.getDate()
        );
        const isInitDisabled = disabledDayKeys.has(formatDateForNewApi(initMid));

        // Convert URL params to UTC ISO strings for preferred event matching
        const preferStart1 = urlParams.depart
          ? estonianToUtcIso(urlParams.depart) ?? undefined
          : undefined;
        const preferStart2 = urlParams.return
          ? estonianToUtcIso(urlParams.return) ?? undefined
          : undefined;

        const hasUrlParams = Boolean(preferStart1 || preferStart2);
        const firstAvailableDate = parseCalendarDateFromApi(firstAvailableForAll);
        // firstAvailableDate may be outside requested range if backend returns global first-available.
        // We trust it: calendar will show that date; fetchAllEvents will load events for it.
        const dateToTry = hasUrlParams
          ? initialDate
          : firstAvailableDate ?? (isInitDisabled ? null : initialDate);

        if (dateToTry) {
          const initialResult = await fetchAllEvents(
            dateToTry,
            false,
            preferStart1,
            preferStart2,
            migrateExternalCart
          );

          if (initialResult) {
            setSelectedDate(dateToTry);
            setError(null);
          } else {
            setError("no_events");
            setGroup1Events([]);
            setGroup2Events([]);
          }
        } else {
          setError("no_events");
          setGroup1Events([]);
          setGroup2Events([]);
        }
      } catch (err) {
        console.error("Error fetching disabled dates:", err);
        if (requestId === disabledDatesRequestIdRef.current) {
          setDisabledDates([]);
        }

        if (skipEventFetch) return;

        const preferStart1 = urlParams.depart
          ? estonianToUtcIso(urlParams.depart) ?? undefined
          : undefined;
        const preferStart2 = urlParams.return
          ? estonianToUtcIso(urlParams.return) ?? undefined
          : undefined;
        const found = await fetchAllEvents(
          initialDate,
          false,
          preferStart1,
          preferStart2,
          migrateExternalCart
        );
        if (!found) {
          setError("no_events");
          setGroup1Events([]);
          setGroup2Events([]);
        }
      }
    },
    [groupHash1, groupHash2, isBuilder, fetchAllEvents, urlParams, initialDate]
  );

  // ── Ensure styles are loaded before complex rendering ─────────────────

  useLayoutEffect(() => {
    // Mark styles as loaded after component mounts to prevent layout calculations before CSS is applied
    setStylesLoaded(true);
  }, []);

  // ── Initial load ──────────────────────────────────────────────────

  useEffect(() => {
    if (!groupHash1 && !groupHash2) {
      setError("No event groups selected");
      setLoading(false);
      return;
    }
    setLoading(true);

    // Use the earlier of today and initialDate so the range always covers both
    const rangeBase = initialDate < today ? initialDate : today;
    const startOfMonth = new Date(
      rangeBase.getFullYear(),
      rangeBase.getMonth(),
      1
    );
    const endOf6 = new Date(startOfMonth);
    endOf6.setMonth(endOf6.getMonth() + 6);
    endOf6.setDate(0);

    const startBuf = new Date(startOfMonth);
    startBuf.setDate(startBuf.getDate() - 7);
    const endBuf = new Date(endOf6);
    endBuf.setDate(endBuf.getDate() + 7);

    fetchDisabledDates(startBuf, endBuf, { migrateExternalCart: true }).finally(() => setLoading(false));
  }, [groupHash1, groupHash2]);

  // ── Keep URL in sync with selected events ─────────────────────────
  //
  // Whenever the user changes events or date we write the current
  // depart/return params so the URL is always shareable.
  // We use `replace: true` to avoid bloating browser history.

  useEffect(() => {
    if (!groupHash1 && !groupHash2) return; // Not configured — don't pollute URL

    // During initial hydration, keep inbound depart/return params intact
    // until URL-driven event selection has had a chance to resolve.
    const hasInboundTripParams = Boolean(urlParams.depart || urlParams.return);
    const hasSelectedTripEvents = Boolean(selectedEvent1 || selectedEvent2);
    if (loading && hasInboundTripParams && !hasSelectedTripEvents) {
      return;
    }

    const sp = new URLSearchParams(location.search);

    if (selectedEvent1 && group1Enabled) {
      const estTime = utcIsoToEstonian(
        new Date(selectedEvent1.start).toISOString()
      );
      if (estTime) {
        sp.set("depart", estTime);
      } else {
        sp.delete("depart");
      }
    } else {
      sp.delete("depart");
    }

    if (selectedEvent2 && group2Enabled) {
      const estTime = utcIsoToEstonian(
        new Date(selectedEvent2.start).toISOString()
      );
      if (estTime) {
        sp.set("return", estTime);
      } else {
        sp.delete("return");
      }
    } else {
      sp.delete("return");
    }

    const newSearch = sp.toString().replace(/%3A/gi, ":");
    const currentSearch = location.search.replace(/^\?/, "");
    // Only push if something actually changed
    if (newSearch !== currentSearch) {
      navigate(
        {
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : "",
        },
        { replace: true }
      );
    }
  }, [
    selectedEvent1,
    selectedEvent2,
    group1Enabled,
    group2Enabled,
    groupHash1,
    groupHash2,
    loading,
    urlParams.depart,
    urlParams.return,
  ]);
  // Note: intentionally omitting navigate and location from deps to prevent loops.
  // navigate is stable; location.search is read inside but we don't want to re-run
  // this effect when the URL changes (that would create a feedback loop).

  // ── Calendar handlers ─────────────────────────────────────────────

  const handleCalendarChange = (value: any) => {
    if (value instanceof Date) {
      setSelectedDate(value);
      // Migrate existing cart items to the newly auto-selected events
      fetchAllEvents(value, true, undefined, undefined, false, true);
    }
  };

  const handleActiveStartDateChange = ({
    activeStartDate,
  }: {
    activeStartDate: Date | null;
  }) => {
    if (!activeStartDate) return;
    const start = new Date(
      activeStartDate.getFullYear(),
      activeStartDate.getMonth(),
      1
    );
    const end = new Date(start);
    end.setMonth(end.getMonth() + 6);
    end.setDate(0);

    const startBuf = new Date(start);
    startBuf.setDate(start.getDate() - 7);
    const endBuf = new Date(end);
    endBuf.setDate(end.getDate() + 7);

    fetchDisabledDates(startBuf, endBuf, { skipEventFetch: true });
  };

  const isDateDisabled = ({ date }: { date: Date }) => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return disabledDateTimeSet.has(d.getTime());
  };

  // Mark non-disabled future dates as "available" (light green)
  const getTileClassName = ({
    date,
    view,
  }: {
    date: Date;
    view: string;
  }): string | null => {
    if (view !== "month") return null;
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayMid = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    if (d < todayMid) return null; // past dates – no colour
    const isDisabled = disabledDateTimeSet.has(d.getTime());
    if (isDisabled) return null;
    return "event-round-trip-tile-available";
  };

  // ── Time constraint for round-trip mode ─────────────────────────────

  /**
   * In roundTrip mode:
   *  - Group 2 events that START before Group 1 selected event ENDS → disabled
   *  - Group 1 events where NO Group 2 event starts after their end → disabled
   */
  const timeConstrainedEvents = useMemo(() => {
    const group1Disabled = new Set<string>();
    const group2Disabled = new Set<string>();

    if (tripMode !== "roundTrip") return { group1Disabled, group2Disabled };

    // Disable Group 2 events that start before selected Group 1 event ends
    if (selectedEvent1) {
      const endTime = new Date(selectedEvent1.end).getTime();
      for (const event of group2VisibleEvents) {
        if (new Date(event.start).getTime() < endTime) {
          const eventRef = event?.hash;
          if (eventRef) group2Disabled.add(eventRef);
        }
      }
    }

    // Disable Group 1 events that have NO valid Group 2 event after them
    for (const event of group1VisibleEvents) {
      const endTime = new Date(event.end).getTime();
      const hasValidB = group2VisibleEvents.some(
        (e) => new Date(e.start).getTime() >= endTime
      );
      if (!hasValidB) {
        const eventRef = event?.hash;
        if (eventRef) group1Disabled.add(eventRef);
      }
    }

    return { group1Disabled, group2Disabled };
  }, [tripMode, selectedEvent1, group1VisibleEvents, group2VisibleEvents]);

  // Keep enabled groups selected; clear selection from disabled groups.
  useEffect(() => {
    const visible1Refs = new Set(
      group1VisibleEvents
        .map((e: any) => e?.hash)
        .filter((ref: string | undefined): ref is string => Boolean(ref))
    );
    const visible2Refs = new Set(
      group2VisibleEvents
        .map((e: any) => e?.hash)
        .filter((ref: string | undefined): ref is string => Boolean(ref))
    );

    const eventKey = (event: any) => event?.hash ?? null;

    let resolvedE1 =
      selectedEvent1 && visible1Refs.has(selectedEvent1?.hash)
        ? selectedEvent1
        : null;

    if (group1Enabled && !resolvedE1 && group1VisibleEvents.length > 0) {
      resolvedE1 = group1VisibleEvents[0];
    }

    if (!group1Enabled && selectedEvent1) {
      setSelectedEvent1(null);
    } else if (group1Enabled && eventKey(resolvedE1) !== eventKey(selectedEvent1)) {
      setSelectedEvent1(resolvedE1);
    }

    let resolvedE2 =
      selectedEvent2 && visible2Refs.has(selectedEvent2?.hash)
        ? selectedEvent2
        : null;

    if (group2Enabled && !resolvedE2) {
      let fallback: any = null;
      if (group2VisibleEvents.length > 0) {
        if (tripMode === "roundTrip" && resolvedE1) {
          const endTime = new Date(resolvedE1.end).getTime();
          fallback =
            group2VisibleEvents.find(
              (e) => new Date(e.start).getTime() >= endTime
            ) || group2VisibleEvents[0];
        } else {
          fallback = group2VisibleEvents[0];
        }
      }
      resolvedE2 = fallback;
    }

    if (!group2Enabled && selectedEvent2) {
      setSelectedEvent2(null);
    } else if (group2Enabled && eventKey(resolvedE2) !== eventKey(selectedEvent2)) {
      setSelectedEvent2(resolvedE2);
    }
  }, [
    group1VisibleEvents,
    group2VisibleEvents,
    selectedEvent1,
    selectedEvent2,
    tripMode,
    group1Enabled,
    group2Enabled,
  ]);

  // ── Event selection handler ───────────────────────────────────────

  const handleEventSelect = useCallback(
    (groupNum: 1 | 2, newEvent: any) => {
      const currentSelected = groupNum === 1 ? selectedEvent1 : selectedEvent2;
      const setSelected =
        groupNum === 1 ? setSelectedEvent1 : setSelectedEvent2;

      // Check time constraint — don't allow selecting disabled events
      if (tripMode === "roundTrip") {
        const disabledSet =
          groupNum === 1
            ? timeConstrainedEvents.group1Disabled
            : timeConstrainedEvents.group2Disabled;
        if (disabledSet.has(getEventRef(newEvent))) return;
      }

      // Keep one timeslot selected for enabled groups.
      const newEventRef = getEventRef(newEvent);
      if (
        currentSelected?.hash === newEvent.hash
      ) {
        return;
      }

      // Select new event — move existing cart items from any group event
      const groupEventRefs = getGroupEventRefs(groupNum).filter(
        (ref) => ref !== newEventRef
      );
      if (groupEventRefs.length > 0) {
        moveCartToEvent(groupEventRefs, newEvent);
      }

      setSelected(newEvent);

      // In roundTrip: when Group 1 changes, auto-validate Group 2
      if (groupNum === 1 && tripMode === "roundTrip") {
        const newEndTime = new Date(newEvent.end).getTime();
        if (selectedEvent2) {
          const g2Start = new Date(selectedEvent2.start).getTime();
          if (g2Start < newEndTime) {
            // Current Group 2 selection is now invalid
            const firstValid = group2VisibleEvents.find(
              (e) => new Date(e.start).getTime() >= newEndTime
            );
            if (firstValid) {
              const firstValidRef = getEventRef(firstValid);
              const g2EventRefs = getGroupEventRefs(2).filter(
                (ref) => ref !== firstValidRef
              );
              if (g2EventRefs.length > 0)
                moveCartToEvent(g2EventRefs, firstValid);
            } else {
              clearCartForGroup(2);
            }
            setSelectedEvent2(firstValid || null);
          }
        } else {
          // No Group 2 selected — auto-select first valid
          const firstValid = group2VisibleEvents.find(
            (e) => new Date(e.start).getTime() >= newEndTime
          );
          setSelectedEvent2(firstValid || null);
        }
      }
    },
    [
      selectedEvent1,
      selectedEvent2,
      tripMode,
      group2VisibleEvents,
      timeConstrainedEvents,
      getGroupEventRefs,
      getEventRef,
      moveCartToEvent,
      clearCartForGroup,
    ]
  );

  // ── Mode toggle ───────────────────────────────────────────────────

  const handleTripModeChange = (mode: "roundTrip" | "oneWay") => {
    setTripMode(mode);
    if (mode === "oneWay") {
      setGroup1Enabled(true);
      setGroup2Enabled(false);
      // Clear Group 2 cart items — user is going one way
      clearCartForGroup(2);
      setSelectedEvent2(null);
      if (!selectedEvent1 && group1VisibleEvents.length > 0) {
        setSelectedEvent1(group1VisibleEvents[0]);
      }
    } else {
      // roundTrip — enable both groups
      setGroup1Enabled(true);
      setGroup2Enabled(true);
      const resolvedE1 =
        selectedEvent1 &&
        group1VisibleEvents.some(
          (e) => getEventRef(e) === getEventRef(selectedEvent1)
        )
          ? selectedEvent1
          : group1VisibleEvents[0] ?? null;
      if (getEventRef(resolvedE1) !== getEventRef(selectedEvent1)) {
        setSelectedEvent1(resolvedE1);
      }
      // Auto-select valid Group 2 event based on Group 1's end time
      if (resolvedE1) {
        const endTime = new Date(resolvedE1.end).getTime();
        const firstValid = group2VisibleEvents.find(
          (e) => new Date(e.start).getTime() >= endTime
        );
        setSelectedEvent2(
          firstValid ||
            (group2VisibleEvents.length > 0 ? group2VisibleEvents[0] : null)
        );
      }
    }
  };

  // ── Group header click (oneWay mode only) ─────────────────────────

  const handleGroup1HeaderClick = () => {
    if (tripMode === "roundTrip") return;
    if (!group1Enabled) {
      const targetEvent =
        selectedEvent1 ??
        (group1VisibleEvents.length > 0 ? group1VisibleEvents[0] : null);
      if (targetEvent) {
        const group2EventRefs = getGroupEventRefs(2).filter(
          (ref) => ref !== getEventRef(targetEvent)
        );
        if (group2EventRefs.length > 0) {
          moveCartToEvent(group2EventRefs, targetEvent);
        }
      } else {
        clearCartForGroup(2);
      }
      setGroup1Enabled(true);
      setGroup2Enabled(false);
      setSelectedEvent2(null);
      setSelectedEvent1(targetEvent);
    }
  };

  const handleGroup2HeaderClick = () => {
    if (tripMode === "roundTrip") return;
    if (!group2Enabled) {
      const targetEvent =
        selectedEvent2 ??
        (group2VisibleEvents.length > 0 ? group2VisibleEvents[0] : null);
      if (targetEvent) {
        const group1EventRefs = getGroupEventRefs(1).filter(
          (ref) => ref !== getEventRef(targetEvent)
        );
        if (group1EventRefs.length > 0) {
          moveCartToEvent(group1EventRefs, targetEvent);
        }
      } else {
        clearCartForGroup(1);
      }
      setGroup2Enabled(true);
      setGroup1Enabled(false);
      setSelectedEvent1(null);
      setSelectedEvent2(targetEvent);
    }
  };

  // ── Render event row ──────────────────────────────────────────────

  const renderEventRow = (
    event: any,
    isSelected: boolean,
    onSelect: () => void,
    groupNum: 1 | 2,
    isTimeDisabled: boolean
  ) => {
    const time = formatTime(event.start);
    const duration = event.end ? formatDuration(event.start, event.end) : "";
    const eventMax = getEventMaxAvailable(event);
    const visibleTicketProducts = getVisibleTicketProducts(event);

    const eventRef = getEventRef(event);
    return (
      <div
        key={eventRef ?? `${event.start}-${event.end}`}
        className={`event-round-trip-row ${
          isSelected ? "event-round-trip-row--selected" : ""
        } ${isTimeDisabled ? "event-round-trip-row--time-disabled" : ""}`}
      >
        {/* Event header row */}
        <div className="event-round-trip-row-header">
          <div className="event-round-trip-row-info">
            <span className="event-round-trip-row-time">{time}</span>
            {duration && (
              <span className="event-round-trip-row-duration">{duration}</span>
            )}
            {eventMax !== null && (
              <span className="event-round-trip-row-seats">
                {isSelected ? t.free_seats : t.available}: {eventMax}
              </span>
            )}
          </div>
          <button
            className={`event-round-trip-select-btn ${
              isSelected
                ? "event-round-trip-select-btn--selected"
                : "event-round-trip-select-btn--select"
            }`}
            onClick={onSelect}
            disabled={isTimeDisabled}
          >
            {isSelected ? t.selected : t.select}
          </button>
        </div>

        {/* Variation products (only when selected) */}
        {isSelected && visibleTicketProducts.length > 0 && (
          <div className="event-round-trip-products">
            {visibleTicketProducts.map((product: any) => {
              const { productMax, displayAvailable } = getTicketProductData(product);
              // Effective max considers BOTH individual product limit and event-level cap
              const effectiveMax = getEffectiveMax(
                productMax,
                event,
                product
              );
              const currentQty = getCartQty(product, eventRef);
              const enforceFirstOrderMin = isFirstOrderMinQtyEnforced(eventRef);
              const effectiveMin = getTicketEffectiveMin(
                product,
                enforceFirstOrderMin
              );
              const minForUi = effectiveMin > 1 ? effectiveMin : undefined;
              const isSoldOut = isEffectivelySoldOut(
                minForUi,
                effectiveMax
              );
              const pickerDisplayAvailable = resolvePickerDisplayAvailable(
                minForUi,
                effectiveMax,
                displayAvailable
              );

              // Local input value (for when user is actively typing)
              const inputKey = getInputKey(product, eventRef);
              const localInputVal = inputValues[inputKey];
              const displayValue =
                localInputVal !== undefined
                  ? localInputVal
                  : String(currentQty);

              return (
                <div
                  key={`${product.uuid}-${eventRef}`}
                  className="event-round-trip-product-row"
                >
                  {/* Name + available */}
                  <div
                    className={`event-round-trip-product-name ${
                      isSoldOut ? "event-round-trip-product-name--sold-out" : ""
                    }`}
                  >
                    <span
                      style={{
                        display: "inline",
                        overflowWrap: "break-word",
                        wordBreak: "break-word",
                      }}
                    >
                      {product.productName}
                      {product.shortDescription && (
                        <>
                          {"\u00A0"}
                          <span
                            className="event-round-trip-info-icon"
                            style={{
                              display: "inline-flex",
                              verticalAlign: "middle",
                            }}
                          onMouseEnter={(e) => {
                            const el = e.currentTarget;
                            setTooltip((prev) =>
                              prev?.pinned
                                ? prev
                                : calcTooltip(el, product.shortDescription, false)
                            );
                          }}
                          onMouseLeave={() => {
                            setTooltip((prev) =>
                              prev?.pinned ? prev : null
                            );
                          }}
                          onTouchStart={(e) => {
                            // Stop propagation so the document touchstart
                            // listener doesn't close the tooltip before onClick fires
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const el = e.currentTarget;
                            setTooltip((prev) =>
                              prev?.pinned
                                ? null
                                : calcTooltip(el, product.shortDescription, true)
                            );
                          }}
                        >
                          <Info style={{ fontSize: "18px", color: "#333" }} />
                        </span>
                        </>
                      )}
                    </span>
                    <div className="event-round-trip-product-available">
                      {t.available}: {pickerDisplayAvailable}
                    </div>
                  </div>

                  {/* Price */}
                  <div
                    className={`event-round-trip-product-price ${
                      isSoldOut
                        ? "event-round-trip-product-price--sold-out"
                        : ""
                    }`}
                  >
                    {formatPrice(product.price)}&nbsp;€
                  </div>

                  {/* Quantity controls or sold-out */}
                  {!isSoldOut ? (
                    <div
                      className="event-round-trip-qty-controls"
                      style={{
                        border:
                          currentQty > 0
                            ? "1px solid  var(--shop-theme-color, #c2185b)"
                            : "1px solid #e2e8f0",
                      }}
                    >
                      <button
                        className="event-round-trip-qty-btn"
                        style={{
                          fontSize: "14px",
                        }}
                        disabled={currentQty <= 0}
                        onClick={() => handleDecrease(product, event)}
                      >
                        −
                      </button>
                      <input
                        className="event-round-trip-qty-input"
                        type="text"
                        inputMode="numeric"
                        min="0"
                        max={effectiveMax}
                        value={displayValue}
                        onChange={(e) =>
                          handleQtyInputChange(e, product, event, effectiveMax)
                        }
                        onBlur={() => handleQtyInputBlur(product, event)}
                      />
                      <button
                        className="event-round-trip-qty-btn"
                        style={{
                          fontSize: "14px",
                        }}
                        disabled={currentQty >= effectiveMax}
                        onClick={() => handleIncrease(product, event)}
                      >
                        +
                      </button>
                    </div>
                  ) : (
                    <div className="event-round-trip-sold-out">
                      {t.sold_out}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── Render column ─────────────────────────────────────────────────

  const renderColumn = (
    groupNum: 1 | 2,
    events: any[],
    loadingEvents: boolean,
    selectedEvent: any,
    enabled: boolean,
    headerName: string,
    onHeaderClick: () => void,
    disabledEventRefs: Set<string>
  ) => {
    return (
      <div className="event-round-trip-column-wrapper">
        {/* Header is clickable only in one-way mode */}
        <div
          className={`event-round-trip-column-header ${
            !enabled ? "event-round-trip-column-header--disabled" : ""
          }`}
          onClick={onHeaderClick}
          style={{
            cursor:
              tripMode === "roundTrip"
                ? "default"
                : !enabled
                ? "pointer"
                : "default",
          }}
        >
          <span className="event-round-trip-column-header-label">
            <Check
              className={`event-round-trip-column-check ${
                enabled
                  ? "event-round-trip-column-check--active"
                  : "event-round-trip-column-check--inactive"
              }`}
              aria-hidden
            />
            <span>{headerName}</span>
          </span>
        </div>

        <div
          className={`event-round-trip-column-body ${
            !enabled ? "event-round-trip-column-body--disabled" : ""
          }`}
        >
          {loadingEvents ? (
            <div className="event-round-trip-empty">{t.loading}</div>
          ) : events.length > 0 ? (
            events.map((event) => {
              const isTimeDisabled = disabledEventRefs.has(getEventRef(event));
              const isSelected =
                selectedEvent?.hash === event.hash;
              return renderEventRow(
                event,
                isSelected,
                () => {
                  if (!enabled || isTimeDisabled) return;
                  handleEventSelect(groupNum, event);
                },
                groupNum,
                isTimeDisabled
              );
            })
          ) : (
            <div className="event-round-trip-empty">{t.no_events}</div>
          )}
        </div>
      </div>
    );
  };

  // ── Loading state ─────────────────────────────────────────────────

  if (loading) {
    return (
      <Div
        id={id.toString()}
        key={id}
        data-style={attributes?.styles || {}}
        className={
          attributes &&
          attributes.classes +
            (attributes.classes !== "container" &&
            attributes.classes !== "container-fluid"
              ? " builder-component builder-component__event_round_trip_component"
              : "") +
            (matchesEditorTarget({ id, uuid }, editorId) ? " builder-component--active" : "")
        }
        data-component-type="section"
      >
        <div className="event-round-trip-loading">
          <SimpleSpinner />
          <p style={{ color: "#718096", margin: 0, fontSize: "15px" }}>
            {t.loading_info}
          </p>
        </div>
      </Div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────

  if (error && group1Events.length === 0 && group2Events.length === 0) {
    return (
      <Div
        id={id.toString()}
        key={id}
        data-style={attributes?.styles || {}}
        className={
          attributes &&
          attributes.classes +
            (attributes.classes !== "container" &&
            attributes.classes !== "container-fluid"
              ? " builder-component builder-component__event_round_trip_component"
              : "") +
            (matchesEditorTarget({ id, uuid }, editorId) ? " builder-component--active" : "")
        }
        data-component-type="section"
      >
        <RenderChildren
          key={id + "children"}
          childWebpageComponentRelations={childWebpageComponentRelations}
        />
        <p
          style={{
            fontSize: "15px",
            textAlign: "center",
            fontWeight: 500,
            color: "#212529",
          }}
        >
          {error === "no_events" ? t.no_events : t.error}
        </p>
      </Div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────

  const tooltipPortal = tooltip
    ? ReactDOM.createPortal(
        <div
          ref={tooltipRef}
          className="event-round-trip-info-tooltip"
          style={{
            top: tooltip.top,
            left: tooltip.left,
            visibility: tooltip.positionReady ? "visible" : "hidden",
          }}
          dangerouslySetInnerHTML={{ __html: tooltip.html }}
        />,
        document.body
      )
    : null;

  return (
    <>
      {tooltipPortal}
    <Div
      id={id.toString()}
      key={id}
      data-style={attributes?.styles || {}}
      className={
        attributes &&
        attributes.classes +
          (attributes.classes !== "container" &&
          attributes.classes !== "container-fluid"
            ? " builder-component builder-component__event_round_trip_component"
            : "") +
          (matchesEditorTarget({ id, uuid }, editorId) ? " builder-component--active" : "")
      }
      data-component-type="section"
      style={{ height: "100%" }}
    >
      <RenderChildren
        key={id + "children"}
        childWebpageComponentRelations={childWebpageComponentRelations}
      />

      {stylesLoaded && (
        <div
          style={{
            color: "#212529",
            fontFamily: "Roboto, sans-serif",
            fontSize: "15px",
          }}
        >
          {/* Calendar + content layout */}
          <div className="event-round-trip-layout">
            {/* Calendar */}
            <div>
              <Calendar
                className="event-round-trip-calendar"
                minDate={new Date()}
                next2Label={null}
                prev2Label={null}
                onChange={handleCalendarChange}
                onActiveStartDateChange={handleActiveStartDateChange}
                value={selectedDate}
                locale="et-EE"
                tileDisabled={isDateDisabled}
                tileClassName={getTileClassName}
                formatShortWeekday={(_locale, date) => {
                  const days = ["P", "E", "T", "K", "N", "R", "L"];
                  return days[date.getDay()];
                }}
                formatMonth={(_locale, date) => {
                  const months = [
                    "Jaanuar",
                    "Veebruar",
                    "Märts",
                    "Aprill",
                    "Mai",
                    "Juuni",
                    "Juuli",
                    "August",
                    "September",
                    "Oktoober",
                    "November",
                    "Detsember",
                  ];
                  return months[date.getMonth()];
                }}
              />
            </div>

            {/* Right content */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
            >
              {/* Header: date + mode buttons */}
              <div className="event-round-trip-header">
                <h4 className="event-round-trip-date-title">
                  {selectedDate
                    .toLocaleDateString("et-EE", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })
                    .replace(/^[a-z]/, (c) => c.toUpperCase())}
                </h4>

                <div className="event-round-trip-mode-buttons">
                  <button
                    className={`event-round-trip-mode-btn ${
                      tripMode === "roundTrip"
                        ? "event-round-trip-mode-btn--active"
                        : "event-round-trip-mode-btn--inactive"
                    }`}
                    onClick={() => handleTripModeChange("roundTrip")}
                  >
                    {t.round_trip}
                  </button>
                  <button
                    className={`event-round-trip-mode-btn ${
                      tripMode === "oneWay"
                        ? "event-round-trip-mode-btn--active"
                        : "event-round-trip-mode-btn--inactive"
                    }`}
                    onClick={() => handleTripModeChange("oneWay")}
                  >
                    {t.one_way}
                  </button>
                </div>
              </div>

              {/* Two event columns */}
              <div className="event-round-trip-columns">
                {/* Group 1 column */}
                {renderColumn(
                  1,
                  group1VisibleEvents,
                  loadingGroup1,
                  selectedEvent1,
                  group1Enabled,
                  groupName1,
                  handleGroup1HeaderClick,
                  timeConstrainedEvents.group1Disabled
                )}

                {/* Group 2 column — always rendered, disabled when oneWay or toggled off */}
                {renderColumn(
                  2,
                  group2VisibleEvents,
                  loadingGroup2,
                  selectedEvent2,
                  group2Enabled,
                  groupName2,
                  handleGroup2HeaderClick,
                  timeConstrainedEvents.group2Disabled
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Div>
    </>
  );
}
